import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag, DollarSign, Clock, CheckCircle2,
  TrendingUp, TrendingDown, AlertTriangle, RefreshCw,
  ArrowRight, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import kitchenApi from '../services/kitchenApiService';
import { useKitchenAuth } from '../context/KitchenAuthContext';

const STATUS_COLORS = {
  new:       { bar: '#3B82F6', badge: 'k-badge-new' },
  accepted:  { bar: '#8B5CF6', badge: 'k-badge-accepted' },
  preparing: { bar: '#F59E0B', badge: 'k-badge-preparing' },
  ready:     { bar: '#10B981', badge: 'k-badge-ready' },
  completed: { bar: '#6B7280', badge: 'k-badge-completed' },
  cancelled: { bar: '#EF4444', badge: 'k-badge-cancelled' },
};

function KpiCard({ label, value, trend, trendLabel, icon: Icon, iconColor, iconBg, loading }) {
  const isUp = trend > 0;
  return (
    <div className="k-kpi-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="k-kpi-label">{label}</div>
          {loading ? (
            <div className="k-skeleton" style={{ width: 100, height: 32, marginTop: 8 }} />
          ) : (
            <div className="k-kpi-value">{value}</div>
          )}
        </div>
        <div className="k-kpi-icon-wrap" style={{ background: iconBg }}>
          <Icon size={20} color={iconColor} />
        </div>
      </div>
      {trend !== undefined && !loading && (
        <div className={`k-kpi-trend ${isUp ? 'up' : 'down'}`}>
          {isUp ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          {Math.abs(trend)}% {trendLabel || 'vs last period'}
        </div>
      )}
    </div>
  );
}

function OrderRowSkeleton() {
  return (
    <tr>
      {[1, 2, 3, 4, 5].map(i => (
        <td key={i}><div className="k-skeleton" style={{ height: 14, width: `${[60, 80, 50, 70, 90][i - 1]}%` }} /></td>
      ))}
    </tr>
  );
}

const fmt$ = (n) => `$${(n || 0).toFixed(2)}`;
const fmtNum = (n) => (n || 0).toLocaleString();

export default function DashboardPage() {
  const { user, can } = useKitchenAuth();
  const navigate       = useNavigate();

  const [dashboard, setDashboard]   = useState(null);
  const [loading,   setLoading]     = useState(true);
  const [isOpen,    setIsOpen]      = useState(false);
  const [toggling,  setToggling]    = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [dashRes, statusRes] = await Promise.all([
        kitchenApi.getOrdersDashboard(),
        kitchenApi.getKitchenStatus(),
      ]);
      if (dashRes.success)   setDashboard(dashRes.data);
      if (statusRes.success) setIsOpen(statusRes.data?.is_open ?? statusRes.data?.is_accepting_orders ?? false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setLastRefresh(new Date()); }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const id = setInterval(() => loadDashboard(true), 30000);
    return () => clearInterval(id);
  }, [loadDashboard]);

  const handleToggle = async () => {
    setToggling(true);
    const res = await kitchenApi.toggleKitchenOpen(!isOpen);
    setToggling(false);
    if (res.success) setIsOpen(!isOpen);
  };

  const d = dashboard || {};
  const orders         = d.orders_by_status || d.order_counts || {};
  const todayOrders    = d.today_orders ?? d.total_orders ?? 0;
  const todayRevenue   = d.today_revenue ?? d.total_revenue ?? 0;
  const pendingOrders  = (orders.new || 0) + (orders.accepted || 0) + (orders.preparing || 0);
  const completedToday = orders.completed || 0;
  const avgTime        = d.avg_prep_time_minutes ?? d.avg_preparation_time ?? 0;
  const revenueData    = d.revenue_chart || d.daily_revenue || [];
  const recentOrders   = d.recent_orders || [];
  const notifications  = d.alerts || d.notifications || [];

  return (
    <div>
      {/* Page header */}
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">
            {user?.kitchen_name || 'Kitchen'} Dashboard 🍛
          </h1>
          <p className="k-page-sub">
            Last updated {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <div className="k-page-actions">
          {/* Open/Close toggle */}
          <button
            className={`k-btn ${isOpen ? 'k-btn-success' : 'k-btn-danger'}`}
            style={{ gap: 8 }}
            onClick={handleToggle}
            disabled={toggling}
          >
            {toggling
              ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} />
              : isOpen ? <ToggleRight size={18} /> : <ToggleLeft size={18} />
            }
            {isOpen ? 'Kitchen Open' : 'Kitchen Closed'}
          </button>

          <button className="k-btn-icon" onClick={() => loadDashboard()} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Alert banner */}
      {notifications.length > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={18} color="#D97706" />
          <span style={{ fontSize: 14, color: '#92400E', fontWeight: 500 }}>{notifications[0]?.message || notifications[0]}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="k-kpi-grid">
        <KpiCard
          label="Today's Orders"
          value={fmtNum(todayOrders)}
          trend={d.orders_trend}
          trendLabel="vs yesterday"
          icon={ShoppingBag}
          iconColor="#FF6B35"
          iconBg="#FFF7F3"
          loading={loading}
        />
        <KpiCard
          label="Today's Revenue"
          value={fmt$(todayRevenue)}
          trend={d.revenue_trend}
          trendLabel="vs yesterday"
          icon={DollarSign}
          iconColor="#10B981"
          iconBg="#ECFDF5"
          loading={loading}
        />
        <KpiCard
          label="Pending Orders"
          value={fmtNum(pendingOrders)}
          icon={Clock}
          iconColor="#F59E0B"
          iconBg="#FFFBEB"
          loading={loading}
        />
        <KpiCard
          label="Completed Today"
          value={fmtNum(completedToday)}
          icon={CheckCircle2}
          iconColor="#3B82F6"
          iconBg="#EFF6FF"
          loading={loading}
        />
        <KpiCard
          label="Avg Prep Time"
          value={avgTime ? `${avgTime} min` : '—'}
          icon={Clock}
          iconColor="#8B5CF6"
          iconBg="#F5F3FF"
          loading={loading}
        />
      </div>

      {/* Charts + Recent Orders */}
      <div className="k-grid-2" style={{ marginBottom: 24 }}>
        {/* Revenue Chart */}
        <div className="k-card">
          <div className="k-card-header">
            <div>
              <div className="k-card-title">Revenue Trend</div>
              <div className="k-card-sub">Daily revenue this week</div>
            </div>
          </div>
          {loading ? (
            <div className="k-skeleton" style={{ height: 180 }} />
          ) : revenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={revenueData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#FF6B35" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} labelStyle={{ fontWeight: 600 }} />
                <Area type="monotone" dataKey="revenue" stroke="#FF6B35" strokeWidth={2.5} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="k-empty" style={{ paddingTop: 30, paddingBottom: 30 }}>
              <div className="k-empty-icon">📊</div>
              <div className="k-empty-title">No revenue data yet</div>
            </div>
          )}
        </div>

        {/* Orders by Status */}
        <div className="k-card">
          <div className="k-card-header">
            <div>
              <div className="k-card-title">Orders by Status</div>
              <div className="k-card-sub">Current status breakdown</div>
            </div>
            {can.orders && (
              <button className="k-btn k-btn-sm k-btn-ghost" onClick={() => navigate('/kitchen/orders')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                View All <ArrowRight size={14} />
              </button>
            )}
          </div>
          {loading ? (
            <div className="k-skeleton" style={{ height: 180 }} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(orders).filter(([s]) => ['new','accepted','preparing','ready','completed'].includes(s)).map(([status, count]) => (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 90, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', color: 'var(--k-text-sub)' }}>{status}</div>
                  <div style={{ flex: 1, height: 8, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(100, (count / Math.max(todayOrders || 1, count)) * 100)}%`,
                        background: STATUS_COLORS[status]?.bar || '#94A3B8',
                        borderRadius: 999,
                        transition: 'width .4s ease',
                      }}
                    />
                  </div>
                  <div style={{ width: 28, fontSize: 14, fontWeight: 700, color: 'var(--k-text)', textAlign: 'right' }}>{count}</div>
                </div>
              ))}
              {Object.keys(orders).length === 0 && (
                <div className="k-empty" style={{ paddingTop: 20, paddingBottom: 20 }}>
                  <div className="k-empty-icon">📦</div>
                  <div className="k-empty-title">No orders yet today</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders table */}
      <div className="k-card">
        <div className="k-card-header">
          <div>
            <div className="k-card-title">Recent Orders</div>
            <div className="k-card-sub">Latest 10 orders</div>
          </div>
          {can.orders && (
            <button className="k-btn k-btn-sm k-btn-ghost" onClick={() => navigate('/kitchen/orders')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              View All <ArrowRight size={14} />
            </button>
          )}
        </div>
        <div className="k-table-wrap">
          <table className="k-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(0).map((_, i) => <OrderRowSkeleton key={i} />)
              ) : recentOrders.length > 0 ? (
                recentOrders.map(order => (
                  <tr
                    key={order.order_id || order.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/kitchen/orders/${order.order_id || order.id}`)}
                  >
                    <td style={{ fontWeight: 700, color: 'var(--k-primary)', fontFamily: 'monospace', fontSize: 13 }}>
                      #{String(order.order_id || order.id).padStart(5, '0')}
                    </td>
                    <td style={{ color: 'var(--k-text)' }}>{order.customer_name || order.customer?.name || '—'}</td>
                    <td style={{ color: 'var(--k-text-sub)' }}>{order.item_count || order.items?.length || '—'} item{(order.item_count || 1) !== 1 ? 's' : ''}</td>
                    <td style={{ fontWeight: 600 }}>{fmt$(order.total_amount || order.total)}</td>
                    <td>
                      <span className={`k-badge k-badge-${(order.status || 'new').toLowerCase().replace(' ', '_')}`}>
                        {order.status || 'new'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--k-text-sub)', fontSize: 12 }}>
                      {order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="k-empty">
                      <div className="k-empty-icon">🍽️</div>
                      <div className="k-empty-title">No orders yet</div>
                      <div className="k-empty-sub">Orders placed by customers will appear here</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
